import { useMemo } from "react";

import {
  useEsaExperience,
  type FundingProgramPropertiesData,
} from "../providers/EsaExperienceProvider";
import { findFundingProgramForState } from "./findFundingProgramForState";

// TODO: Abbreviation should be migrated to Slug in the future.
export type FundingProgramPropertiesResult = {
  programs: FundingProgramPropertiesData[];
  loading: boolean;
  findByAbbreviation: (
    abbreviation: string | undefined | null,
  ) => FundingProgramPropertiesData | undefined;
  findByState: (
    stateAbbreviation: string | undefined | null,
  ) => FundingProgramPropertiesData | undefined;
  findByInstitution: (
    institution: string | undefined | null,
  ) => FundingProgramPropertiesData | undefined;
  findByLandingPagePath: (
    path: string | undefined | null,
  ) => FundingProgramPropertiesData | undefined;
  getWaitPeriodDays: (abbreviation: string | undefined | null) => number;
  getProgramEndDate: (abbreviation: string | undefined | null) => Date | null;
  classWalletAbbreviations: string[];
};

export function useFundingProgramProperties(): FundingProgramPropertiesResult {
  const { fundingPrograms: programs, fundingProgramsLoading: loading } =
    useEsaExperience();

  return useMemo(() => {
    function findByAbbreviation(
      abbreviation: string | undefined | null,
    ): FundingProgramPropertiesData | undefined {
      if (!abbreviation) {
        return undefined;
      }
      return programs.find(program => program.abbreviation === abbreviation);
    }

    function findByState(
      stateAbbreviation: string | undefined | null,
    ): FundingProgramPropertiesData | undefined {
      return findFundingProgramForState(programs, stateAbbreviation);
    }

    function findByInstitution(
      institution: string | undefined | null,
    ): FundingProgramPropertiesData | undefined {
      if (!institution) {
        return undefined;
      }
      const normalized = institution.toLowerCase().trim();
      return programs.find(
        p => p.institution?.toLowerCase().trim() === normalized,
      );
    }

    function findByLandingPagePath(
      path: string | undefined | null,
    ): FundingProgramPropertiesData | undefined {
      if (!path) {
        return undefined;
      }
      return programs.find(
        p => p.landingPagePath && path.includes(p.landingPagePath),
      );
    }

    function getWaitPeriodDays(
      abbreviation: string | undefined | null,
    ): number {
      return findByAbbreviation(abbreviation)?.waitPeriodDays ?? 0;
    }

    function getProgramEndDate(
      abbreviation: string | undefined | null,
    ): Date | null {
      const endDate = findByAbbreviation(abbreviation)?.programEndDate;
      return endDate ? new Date(endDate) : null;
    }

    const classWalletAbbreviations = programs
      .filter(p => p.isClassWallet)
      .map(p => p.abbreviation);

    return {
      programs,
      loading,
      findByAbbreviation,
      findByState,
      findByInstitution,
      findByLandingPagePath,
      getWaitPeriodDays,
      getProgramEndDate,
      classWalletAbbreviations,
    };
  }, [programs, loading]);
}
